/* ============================================================================
   <rtb-pdp> — the PDP behaviour Horizon's own components do not already own.

   Load it with `defer` (classic script, not a module):
       <script src="{{ 'rtb-product.js' | asset_url }}" defer></script>

   RELATIONSHIP TO HORIZON
   Add-to-cart, the variant picker, the price, the gallery, the quantity
   stepper and the cart drawer are Horizon components rendered inside this
   section (<product-form-component>, <variant-picker>, <product-price>,
   <media-gallery>, <quantity-selector-component>, <cart-drawer-component>).
   Nothing here fetches, mutates the cart, listens for a cart event or touches
   the quantity buttons; Horizon's quantity selector sets the native `disabled`
   at its bounds and the section stylesheet skins that state. The jobs below
   are the gaps:

     1. ACCORDION. The disclosure rows are RTB markup, because their content
        comes from product metafields and a row with no source must not render
        at all. Panels are in the initial HTML at all times; this only flips
        attributes.

     2. THE GALLERY'S ACCESSIBLE STATE. Repairs to Horizon markup that cannot
        be made where it is written (slideshow.js, slideshow-controls.liquid and
        product-media*.liquid are Horizon core):

        a. aria-current on the thumbnails. Horizon marks the active thumbnail
           with aria-selected, which means nothing outside a listbox, tablist
           or grid. It is mirrored onto aria-current="true". The CSS ring keys
           off aria-selected, the accessible state off aria-current.
        b. Alt text on the main image. Shopify media alt is empty until a
           merchant types one; an empty alt is filled from the <h1>.
        c. Alt text on the thumbnails is blanked: the thumbnail <button> is
           already named ("Slide 2 of 5"), so the image would name it twice.
        d. The image change is announced. Selecting a thumbnail swaps the
           page's main content and moves no focus, so the new slide's position
           and alt are written into the polite status region beside the
           gallery. The opening slide is recorded, never announced.

     3. SHARE. navigator.share when the browser has it, clipboard copy when it
        does not or when the share fails for any reason other than the user
        dismissing it. No third-party bytes, no pre-consent network call.

   A11Y CONTRACT
   Accordion buttons carry aria-expanded and aria-controls. Status messages go
   through one helper (`_announce`, over RTB.a11y.announce from
   assets/rtb-a11y.js) into role="status" regions, cleared and rewritten on a
   later tick so a repeated message is announced again.

   EXPECTED MARKUP
     <rtb-pdp>
       <div class="rtb-pdp__gallery">
         <media-gallery>…</media-gallery>
         <p role="status" aria-live="polite" data-rtb-gallery-status
            data-gallery-message="Image [index] of [count]"></p>
       </div>
       <h1 class="rtb-pdp__title">…</h1>
       <div data-rtb-accordion>
         <button data-rtb-accordion-button aria-expanded="false"
                 aria-controls="panel-id">…</button>
         <div id="panel-id" data-open="false">…</div>
       </div>
       <button type="button" data-rtb-share="https://…" data-share-title="…"
               data-copied-message="…" data-copy-failed-message="…">…</button>
       <p role="status" aria-live="polite" data-rtb-share-status></p>
     </rtb-pdp>

   A custom element rather than DOMContentLoaded: the theme editor re-renders a
   section in place, and connectedCallback / disconnectedCallback give setup
   and teardown without stacking listeners.
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('rtb-pdp')) return;

  var a11y = window.RTB.a11y;
  var GALLERY_STATUS = '[data-rtb-gallery-status]';

  class RtbPdp extends HTMLElement {
    connectedCallback() {
      this._announceRegions = new Set();
      this._frame = 0;
      this._slideIndex = undefined;

      this._onClick = this._handleClick.bind(this);
      this.addEventListener('click', this._onClick);

      /* Observe the RTB wrapper, not <media-gallery>: Horizon replaces
         <media-gallery> wholesale on a variant change, which shows up here as
         a childList record on its parent. The wrapper itself is RTB markup and
         is only replaced when the whole section is, which reconnects this
         element. Thumbnail selection and a morphed alt arrive as attribute
         records through the filter. */
      this._gallery = this.querySelector('.rtb-pdp__gallery');
      this._galleryObserver = new MutationObserver(this._onGalleryMutation.bind(this));
      if (this._gallery) {
        this._galleryObserver.observe(this._gallery, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-selected', 'alt'],
        });
      }

      /* The first pass repairs the server-rendered markup; it is not a
         reaction to a change, so it runs now rather than on a frame. */
      this._repairGallery();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);

      this._galleryObserver.disconnect();
      this._galleryObserver = null;
      this._gallery = null;

      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = 0;

      this._announceRegions.forEach(a11y.cancelAnnounce);
      this._announceRegions.clear();
    }

    /* ---------------------------------------------------------------- events */

    _handleClick(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var accordionButton = target.closest('[data-rtb-accordion-button]');
      if (accordionButton && this.contains(accordionButton)) {
        this._toggleAccordion(accordionButton);
        return;
      }

      var shareButton = target.closest('[data-rtb-share]');
      if (shareButton && this.contains(shareButton)) {
        this._share(shareButton);
      }
    }

    /* ------------------------------------------------------------- accordion */

    /* Single-open, matching the design. Clicking the open row closes it, so
       the all-closed state stays reachable. */
    _toggleAccordion(button) {
      var willOpen = button.getAttribute('aria-expanded') !== 'true';
      var group = button.closest('[data-rtb-accordion]');
      if (!group) return;

      var buttons = group.querySelectorAll('[data-rtb-accordion-button]');
      for (var i = 0; i < buttons.length; i++) {
        this._setAccordionState(buttons[i], false);
      }
      if (willOpen) this._setAccordionState(button, true);
    }

    _setAccordionState(button, open) {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');

      var panelId = button.getAttribute('aria-controls');
      if (!panelId) return;
      var panel = this.querySelector('#' + CSS.escape(panelId));
      if (panel) panel.setAttribute('data-open', open ? 'true' : 'false');
    }

    /* --------------------------------------------------------------- gallery */

    /* A variant change or a slideshow step arrives as a burst of records;
       they are coalesced into one repair per frame. Writes to the status
       region are ours and are ignored. */
    _onGalleryMutation(records) {
      for (var i = 0; i < records.length; i++) {
        if (!records[i].target.closest(GALLERY_STATUS)) {
          this._scheduleRepair();
          return;
        }
      }
    }

    _scheduleRepair() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(
        function () {
          this._frame = 0;
          this._repairGallery();
        }.bind(this)
      );
    }

    /* Only attributes are written here, and only when they differ; the
       records those writes queue are dropped so the pass cannot re-trigger
       itself. */
    _repairGallery() {
      var gallery = this._gallery;
      if (!gallery) return;

      this._syncGalleryAlts(gallery);
      this._syncThumbnails(gallery);

      if (this._galleryObserver) this._galleryObserver.takeRecords();
    }

    _syncGalleryAlts(gallery) {
      var thumbImages = gallery.querySelectorAll('.slideshow-controls__thumbnail img');
      for (var i = 0; i < thumbImages.length; i++) {
        if (thumbImages[i].getAttribute('alt') !== '') thumbImages[i].setAttribute('alt', '');
      }

      var title = this._productTitle();
      if (!title) return;

      var images = gallery.querySelectorAll('slideshow-slide img');
      for (var j = 0; j < images.length; j++) {
        if (images[j].closest('.slideshow-controls__thumbnail')) continue;
        if ((images[j].getAttribute('alt') || '').trim() === '') {
          images[j].setAttribute('alt', title);
        }
      }
    }

    _productTitle() {
      var heading = this.querySelector('.rtb-pdp__title');
      return heading ? heading.textContent.trim() : '';
    }

    /* A single-media variant renders no thumbnails; forgetting the index then
       keeps the next gallery's first sync a silent recording. */
    _syncThumbnails(gallery) {
      var thumbs = gallery.querySelectorAll('.slideshow-controls__thumbnail');
      if (!thumbs.length) {
        this._slideIndex = undefined;
        return;
      }

      var selected = -1;
      for (var i = 0; i < thumbs.length; i++) {
        if (thumbs[i].getAttribute('aria-selected') === 'true') {
          if (thumbs[i].getAttribute('aria-current') !== 'true') {
            thumbs[i].setAttribute('aria-current', 'true');
          }
          selected = i;
        } else if (thumbs[i].hasAttribute('aria-current')) {
          thumbs[i].removeAttribute('aria-current');
        }
      }

      this._announceSlide(gallery, selected, thumbs.length);
    }

    _announceSlide(gallery, index, count) {
      if (index < 0) return;

      if (this._slideIndex === undefined || this._slideIndex === index) {
        this._slideIndex = index;
        return;
      }
      this._slideIndex = index;

      var region = gallery.querySelector(GALLERY_STATUS);
      if (!region) return;

      var message = (region.getAttribute('data-gallery-message') || '')
        .replace('[index]', String(index + 1))
        .replace('[count]', String(count));

      var slides = gallery.querySelectorAll('slideshow-slide');
      var image = slides[index] ? slides[index].querySelector('img') : null;
      var alt = image ? (image.getAttribute('alt') || '').trim() : '';
      if (alt) message = message ? message + ': ' + alt : alt;

      this._announce(region, message);
    }

    /* ----------------------------------------------------------------- share */

    _share(button) {
      var url = button.getAttribute('data-rtb-share') || window.location.href;
      var title = button.getAttribute('data-share-title') || document.title;

      if (!navigator.share) {
        this._copyLink(button, url);
        return;
      }

      navigator.share({ title: title, url: url }).catch(
        function (error) {
          /* A dismissed sheet is not a failure and needs no feedback. */
          if (error && error.name === 'AbortError') return;
          this._copyLink(button, url);
        }.bind(this)
      );
    }

    _copyLink(button, url) {
      var region = this.querySelector('[data-rtb-share-status]');
      var copied = button.getAttribute('data-copied-message') || '';
      var failed = button.getAttribute('data-copy-failed-message') || '';

      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        this._announce(region, failed);
        return;
      }

      navigator.clipboard.writeText(url).then(
        function () {
          this._announce(region, copied);
        }.bind(this),
        function () {
          this._announce(region, failed);
        }.bind(this)
      );
    }

    /* -------------------------------------------------------------- announce */

    /* The one write path into a status region. Regions written to are
       remembered so a pending write is dropped on disconnect. */
    _announce(region, message) {
      if (!region || !message || !this.isConnected) return;

      this._announceRegions.add(region);
      a11y.announce(region, message);
    }
  }

  customElements.define('rtb-pdp', RtbPdp);
})();
